/**
 * AuthSubmit — primary CTA button used at the bottom of every auth form.
 * Standardises sizing, hover lift, disabled/loading state, and spinner
 * across Login, Register, Forgot Password, and Reset Password.
 *
 * Why a dedicated component:
 *   - Three pages were each defining their own variant of the same
 *     button + spinner block, with subtle drift in disabled colors and
 *     loading copy. One component eliminates the drift and makes future
 *     polish (e.g. focus ring) a single-file change.
 *   - The disabled-during-submit pattern ("Prevent double-click issues")
 *     is enforced by the caller passing `loading`; the button locks itself.
 */
import { Button } from '@/components/ui/button';

export default function AuthSubmit({
  loading = false,
  disabled = false,
  loadingLabel,
  children,
  testId,
}) {
  return (
    <Button
      data-testid={testId}
      type="submit"
      disabled={loading || disabled}
      className="auth-btn w-full h-11 text-sm font-semibold rounded-md text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
      style={{ background: 'var(--strata-steel, #4575A8)', fontFamily: 'var(--strata-font-body, Montserrat), sans-serif', letterSpacing: '0.04em' }}
      onMouseEnter={(e) => { if (!loading && !disabled) e.currentTarget.style.background = 'var(--strata-steel-2, #5C8AC2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--strata-steel, #4575A8)'; }}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span
            className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            aria-hidden="true"
          />
          {loadingLabel || 'Working…'}
        </span>
      ) : (
        children
      )}
    </Button>
  );
}
